// 使用者與權限 → 分享給本組織（SPEC #partners-302、#vms-web-801；原 web/partner 主控台併進 VMS）：
//   - 分享請求：組織管理員 / 技師 / 主管產生一次性代碼與連結（7 天），交給家戶；家戶在自己的 App / VMS 同意（組織不能用 email 查人）
//   - 收到的分享：家戶分享給本組織的設備（用途、範圍、條件、期限）；安裝分享可「完成安裝（交屋）」；組織可以終止
//   - 成員能經這些分享做什麼 = 分享範圍 ∩ 成員的服務角色（在「成員」分頁設定），只能縮不能超過分享範圍
// 組織不能管理分享進來的設備（解綁、OTA、改名、再分享都不行，SPEC #device-permissions-isolation）。
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirm } from '../../components/ConfirmDialog';
import { isOrgAdmin, orgShareApi, PURPOSES_BY_KIND, sharePurposes, type MyOrg } from '../../lib/planeApi';
import { errText } from '../settings/OrgSection';
import { condLabel, scopeLabel } from './SharesTab';
import { dateStamp } from '../../lib/timefmt';

/** 能發分享請求：組織管理員，或服務角色有技師 / 主管。 */
export function canIssueRequests(o: Pick<MyOrg, 'role' | 'service_roles'>): boolean {
  return isOrgAdmin(o) || o.service_roles.some((r) => r === 'technician' || r === 'supervisor');
}

export function OrgSharesTab({ org }: { org: MyOrg }) {
  const { t } = useTranslation();
  const { ask, dialog } = useConfirm();
  const qc = useQueryClient();
  const purposes = sharePurposes(org);
  const [purpose, setPurpose] = useState(purposes[0] ?? '');
  const [note, setNote] = useState('');
  const [issued, setIssued] = useState<{ code: string; link: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const received = useQuery({ queryKey: ['org-shares', org.org_id], queryFn: () => orgShareApi.received(org) });
  const requests = useQuery({ queryKey: ['org-share-requests', org.org_id], queryFn: () => orgShareApi.requests(org) });
  const canIssue = canIssueRequests(org) && purposes.length > 0;
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['org-shares', org.org_id] });
    void qc.invalidateQueries({ queryKey: ['org-share-requests', org.org_id] });
  };
  const onErr = (e: unknown) => setErr(errText(t, e));
  const issue = useMutation({
    mutationFn: () => orgShareApi.createRequest(org, { purpose, note: note.trim() || undefined }),
    onSuccess: (r) => {
      setIssued({ code: r.code, link: r.link });
      setNote('');
      refresh();
    },
    onError: onErr,
  });
  const cancel = useMutation({ mutationFn: (id: string) => orgShareApi.cancelRequest(org, id), onSuccess: refresh, onError: onErr });
  const complete = useMutation({ mutationFn: (id: string) => orgShareApi.completeInstall(org, id), onSuccess: refresh, onError: onErr });
  const end = useMutation({ mutationFn: (id: string) => orgShareApi.end(org, id), onSuccess: refresh, onError: onErr });
  const pending = (requests.data ?? []).filter((r) => r.status === 'pending');
  const shares = (received.data ?? []).filter((s) => s.status !== 'ended');
  return (
    <>
      <section className="card">
        <h2 className="section-title">{t('svc.title')}</h2>
        {org.verified_kinds && org.verified_kinds.length > 0 && (
          <div className="row" data-testid="verified-kinds">
            {org.verified_kinds.map((k) => (
              <span key={k} className="badge badge-ok">
                {t('ext.verified', { kind: t(`ext.kind_${k}`, { defaultValue: k }) })}
              </span>
            ))}
            {org.verification_expires_at && (
              <span className="small muted">{t('svc.verified_until', { date: dateStamp(org.verification_expires_at) })}</span>
            )}
          </div>
        )}
        <p className="muted small">
          {purposes.length
            ? t('svc.hint_kinds', { kinds: (org.verified_kinds ?? org.business_kinds).map((k) => t(`ext.kind_${k}`, { defaultValue: k })).join(t('common.list_sep')) })
            : org.verified_kinds && org.business_kinds.some((k) => PURPOSES_BY_KIND[k])
              ? t('svc.unverified')
              : t('svc.no_kinds')}
        </p>
        {err && <p className="error">{err}</p>}
        {canIssue && (
          <div className="row">
            <select className="input" style={{ width: 'auto' }} value={purpose} onChange={(e) => setPurpose(e.target.value)} aria-label={t('svc.purpose')}>
              {purposes.map((p) => (
                <option key={p} value={p}>
                  {t(`ext.purpose_${p}`, { defaultValue: p })}
                </option>
              ))}
            </select>
            <input className="input" placeholder={t('svc.note')} maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} />
            <button className="btn btn-primary btn-sm" disabled={issue.isPending} onClick={() => issue.mutate()} data-testid="issue-request">
              {t('svc.issue')}
            </button>
          </div>
        )}
        {issued && (
          <div className="form" data-testid="issued">
            <p className="notice small">{t('svc.issued')}</p>
            <code className="mono copyable">{issued.code}</code>
            <code className="mono copyable small">{issued.link}</code>
            <div className="row">
              <button className="btn btn-sm" onClick={() => void navigator.clipboard?.writeText(issued.link)}>
                {t('common.copy')}
              </button>
            </div>
          </div>
        )}
        {pending.length > 0 && (
          <>
            <h3 className="small">{t('svc.pending_requests')}</h3>
            <ul className="plain-list">
              {pending.map((r) => (
                <li key={r.request_id} className="row">
                  <span className="chip">{t(`ext.purpose_${r.purpose}`, { defaultValue: r.purpose })}</span>
                  {r.kind === 'renewal' && <span className="badge">{t('svc.renewal')}</span>}
                  {r.note && <span className="small">{r.note}</span>}
                  <span className="muted small">{t('people.expires', { date: dateStamp(r.expires_at) })}</span>
                  <div className="spacer" />
                  {canIssueRequests(org) && (
                    <button className="btn btn-sm" disabled={cancel.isPending} onClick={() => cancel.mutate(r.request_id)}>
                      {t('svc.cancel_request')}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
      <section className="card">
        <h2 className="section-title">{t('svc.received')}</h2>
        <p className="muted small">{t('svc.received_hint')}</p>
        {received.isLoading && <p className="muted">{t('common.loading')}</p>}
        {received.isError && <p className="error">{errText(t, received.error)}</p>}
        {received.data && shares.length === 0 && <p className="muted small">{t('svc.none_received')}</p>}
        <ul className="plain-list">
          {shares.map((s) => (
            <li key={s.share_id} className="rel-card" data-org-share={s.share_id}>
              <div className="row">
                <strong>{s.home_name || t('svc.a_home')}</strong>
                <span className="chip">{t(`ext.purpose_${s.purpose}`, { defaultValue: s.purpose })}</span>
                <span className={'badge ' + (s.status === 'active' ? 'badge-ok' : 'badge-warn')}>{t(`svc.status_${s.status}`, { defaultValue: t('common.state_unknown') })}</span>
                <div className="spacer" />
                {s.expires_at && <span className="muted small">{t('ext.until', { date: dateStamp(s.expires_at) })}</span>}
                {s.status === 'active' && s.purpose === 'install' && (isOrgAdmin(org) || org.service_roles.includes('technician')) && (
                  <button className="btn btn-sm" disabled={complete.isPending} onClick={() => complete.mutate(s.share_id)}>
                    {t('svc.complete_install')}
                  </button>
                )}
                {isOrgAdmin(org) && (
                  <button
                    className="btn btn-sm"
                    disabled={end.isPending}
                    onClick={async () => (await ask({ danger: true, title: t('svc.end_confirm'), action: t('common.action_end') })) && end.mutate(s.share_id)}
                  >
                    {t('svc.end')}
                  </button>
                )}
              </div>
              <div className="small">{s.scopes.map((x) => scopeLabel(t, x)).join(t('common.list_sep'))}</div>
              <div className="small muted">
                {condLabel(t, s.conditions)} · {t('people.targets', { n: (s.targets ?? []).filter((x) => !x.historical).length })}
              </div>
            </li>
          ))}
        </ul>
      </section>
      {dialog}
    </>
  );
}
